/** Supported countries for phone input. Brazil first (default). */
export const COUNTRY_LIST = [
  { code: '55', dial: '+55', name: 'Brasil' },
  { code: '54', dial: '+54', name: 'Argentina' },
  { code: '1', dial: '+1', name: 'EUA / Canadá' },
  { code: '63', dial: '+63', name: 'Filipinas' },
  { code: '595', dial: '+595', name: 'Paraguai' },
  { code: '351', dial: '+351', name: 'Portugal' },
  { code: '598', dial: '+598', name: 'Uruguai' },
];

/** Sorted by code length descending so we match 351 before 35, etc. */
const COUNTRY_CODES_BY_LENGTH = [...COUNTRY_LIST]
  .sort((a, b) => b.code.length - a.code.length)
  .map((c) => c.code);

/**
 * Parse raw international number into country code and national digits.
 * @param {string} raw - e.g. "5515991593465" or "351912345678"
 * @returns {{ code: string, nationalDigits: string } | null}
 */
export function getCountryCodeFromRaw(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 10) return null;
  for (const code of COUNTRY_CODES_BY_LENGTH) {
    if (digits.startsWith(code)) {
      const nationalDigits = digits.slice(code.length);
      if (nationalDigits.length >= 8 && nationalDigits.length <= 12) {
        return { code, nationalDigits };
      }
    }
  }
  return null;
}

/**
 * Normalize phone input to raw international digits (country + national).
 * Handles legacy formats and multiple countries.
 * @param {string} value - From DB or user (may contain spaces, dashes, parentheses).
 * @returns {string|null} - e.g. "5515991593465" or "351912345678", or null.
 */
export function normalizeToRaw(value) {
  if (value == null || typeof value !== 'string') return null;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11 || digits.length === 10) return '55' + digits;
  if ((digits.length === 13 || digits.length === 12) && digits.startsWith('55')) return digits;
  for (const code of COUNTRY_CODES_BY_LENGTH) {
    if (digits.startsWith(code)) {
      const len = digits.length;
      if (len >= 10 && len <= 15) return digits;
    }
  }
  return null;
}

/**
 * Format raw 13-digit phone for display (no country code).
 * @param {string} raw - e.g. "5515991593465"
 * @returns {string} - "(15) 99159-3465" or "" if invalid
 */
export function formatPhoneBr(raw) {
  if (!raw || typeof raw !== 'string' || (raw.length !== 13 && raw.length !== 12) || !raw.startsWith('55')) return '';
  const ddd = raw.slice(2, 4);
  const cell = raw.slice(4);
  if (cell.length !== 9 && cell.length !== 8) return '';
  const beforeDashPosition = cell.length === 9 ? 5 : 4;
  return `(${ddd}) ${cell.slice(0, beforeDashPosition)}-${cell.slice(beforeDashPosition)}`;
}

/**
 * Format full international number for display (with country prefix).
 * @param {string} raw - e.g. "5515991593465" or "351912345678"
 * @returns {string} - "+55 (15) 99159-3465" or "+351 912 345 678"
 */
export function formatPhoneForDisplay(raw) {
  const parsed = getCountryCodeFromRaw(raw);
  if (!parsed) return raw || '';
  if (parsed.code === '55') {
    const formatted = formatPhoneBr(raw);
    return formatted ? '+55 ' + formatted : raw;
  }
  const grouped = parsed.nationalDigits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
  const country = COUNTRY_LIST.find((c) => c.code === parsed.code);
  const prefix = country ? country.dial + ' ' : '+' + parsed.code + ' ';
  return prefix + grouped;
}

/**
 * Validate phone: strict for Brazil, minimal length for others.
 * @param {string} raw - Full international digits.
 * @returns {{ valid: boolean, message?: string }}
 */
export function validatePhone(raw) {
  if (!raw || typeof raw !== 'string') {
    return { valid: false, message: 'Informe o telefone.' };
  }
  if (raw.startsWith('55')) return validatePhoneBr(raw);
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) {
    return { valid: false, message: 'Telefone inválido. Use o formato internacional.' };
  }
  return { valid: true };
}

/**
 * Validate Brazilian cell with DDD (13 digits: 55 + 2 DDD + 9 + 8).
 * @param {string} raw - e.g. "5515991593465"
 * @returns {{ valid: boolean, message?: string }}
 */
export function validatePhoneBr(raw) {
  if (!raw || typeof raw !== 'string') {
    return { valid: false, message: 'Informe o telefone.' };
  }
  if ((raw.length !== 13 && raw.length !== 12) || !raw.startsWith('55')) {
    return { valid: false, message: 'Telefone deve ser celular brasileiro com DDD, ex.: (15) 99159-3465' };
  }
  const ddd = parseInt(raw.slice(2, 4), 10);
  if (ddd < 11 || ddd > 99) {
    return { valid: false, message: 'Telefone deve ser celular brasileiro com DDD, ex.: (15) 99159-3465' };
  }
  /*if (raw[4] !== '9') {
    return { valid: false, message: 'Telefone deve ser celular brasileiro com DDD, ex.: (15) 99159-3465' };
  }*/
  const beforeDashPosition = raw.length === 13 ? 5 : 4;
  const rest = raw.slice(beforeDashPosition);
  if (!/^\d{8}$/.test(rest)) {
    return { valid: false, message: 'Telefone deve ser celular brasileiro com DDD, ex.: (15) 99159-3465' };
  }
  return { valid: true };
}
